// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ChangeScheduleModal from "@eisinc/App/Components/Cart/ChangeScheduleModal";
import RequestCerificationsModal from "@eisinc/App/Components/RequestCerificationsModal/RequestCerificationsModal";
import saveScheduledLines from "@eisinc/App/Handlers/saveScheduledLines";
import { xCheckoutActions, xCheckoutState } from "@eisinc/App/Store/Reducers";
import { xIsAuthenticated } from "@eisinc/App/Store/Selectors/GeneralSelectors";
import { StepperContext } from "@eisinc/App/Widgets/CheckoutStepper/StepperContext";
import DataTable from "@eisinc/MobiusOverrides/DataTable/DataTable";
import DataTableBody from "@eisinc/MobiusOverrides/DataTable/DataTableBody";
import DataTableCell from "@eisinc/MobiusOverrides/DataTable/DataTableCell";
import DataTableRow from "@eisinc/MobiusOverrides/DataTable/DataTableRow";
import DatePicker, { DatePickerPresentationProps, DatePickerState } from "@eisinc/MobiusOverrides/DatePicker";
import { SelectPresentationProps } from "@eisinc/MobiusOverrides/Select";
import TextField, { TextFieldPresentationProps } from "@eisinc/MobiusOverrides/TextField";
import Tooltip, { TooltipPresentationProps } from "@eisinc/MobiusOverrides/Tooltip/Tooltip";
import { CART_TYPES } from "@eisinc/XDL/constants";
import { parseBoolean, xClassNames } from "@eisinc/XDL/Utilities";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import addDays from "@insite/client-framework/Common/Utilities/addDays";
import { convertDateToDateOnlyString } from "@insite/client-framework/Common/Utilities/DateUtilities";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTo";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import setIsPreloadingData from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetIsPreloadingData";
import setRequestedDeliveryDate from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetRequestedDeliveryDate";
import setRequestedPickUpDate from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetRequestedPickUpDate";
import setShippingMethod from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetShippingMethod";
import translate from "@insite/client-framework/Translate";
import { CarrierDto, ShipViaDto } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import { CheckboxProps } from "@insite/mobius/Checkbox";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Radio, { RadioProps } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupProps } from "@insite/mobius/RadioGroup";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { ChangeEvent, FC, useContext, useEffect, useMemo, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { Dispatch } from "redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

// fixed constants from BE
export const enum shipmentDateTypeValues {
    ShipsAsap = "shipsAsap",
    OneDate = "oneDate",
    ScheduledDates = "scheduledDates",
}

const mapStateToProps = (state: ApplicationState & xCheckoutState) => {
    const settingsCollection = getSettingsCollection(state);
    const { session } = state.context;
    const { cartId, requestedDeliveryDate, requestedPickupDate } = state.pages.checkoutReviewAndSubmit;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const cart = cartState.value;
    const fulfillmentMethod = cart?.fulfillmentMethod || session.fulfillmentMethod;
    const billTo = getBillToState(state, cart?.billToId)?.value;

    return {
        cart,
        showShippingMethod: fulfillmentMethod === FulfillmentMethod.Ship,
        showPickUpDate:
            settingsCollection.accountSettings.enableWarehousePickup &&
            settingsCollection.cartSettings.enableRequestPickUpDate &&
            fulfillmentMethod === FulfillmentMethod.PickUp,
        session,
        accountSettings: settingsCollection.accountSettings,
        cartSettings: settingsCollection.cartSettings,
        allowEmptyShipping: settingsCollection.accountSettings.allowEmptyShipping,
        // XNG-Changes
        cartIsLoading: cartState.isLoading,
        carrierAccount: state.xCheckout.carrierAccount,
        additionalDetails: state.xCheckout.additionalDetails,
        orderDeliveryBlock: state.xCheckout.orderDeliveryBlock,
        requestedDeliveryDate,
        requestedPickupDate,
        billTo,
        customSettings: getSettingsCollection(state).customSettings as any,
        canHaveScheduledOrders: xIsAuthenticated(state) && parseBoolean(cart?.properties.canCustomerScheduleOrders),
        scheduleOrderPageUrl: getPageLinkByPageType(state, "ScheduleOrderPage")?.url || "/ScheduleOrder",
        shipmentScheduleFromState: state.xCheckout.shipmentSchedule,
        isShippingTBD: getCurrentPage(state).type === "CheckoutShippingPage" && translate("TBD"),
    };
};

const mapDispatchToProps = {
    setShippingMethod,
    setRequestedDeliveryDate,
    setRequestedPickUpDate,
    setIsPreloadingData,
    setCarrierAccount: (carrierAccount: string) => (dispatch: Dispatch<xCheckoutActions>) => {
        dispatch({
            type: "xCheckout/setCarrierAccount",
            carrierAccount,
        });
    },
    setAdditionalDetails: (additionalDetails: string) => (dispatch: Dispatch<xCheckoutActions>) => {
        dispatch({
            type: "xCheckout/setAdditionalDetails",
            additionalDetails,
        });
    },
    loadBillTo,
    // XNG-Change
    saveScheduledLines,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CheckoutShippingCarrierServiceStyles {
    container?: GridContainerProps;
    centeringWrapper?: InjectableCss;
    noCarriersFoundGridItem?: GridItemProps;
    noCarriersFoundText?: TypographyPresentationProps;
    carrierGridItem?: GridItemProps;
    carrierSelect?: SelectPresentationProps;
    serviceGridItem?: GridItemProps;
    serviceSelect?: SelectPresentationProps;
    deliveryDateGridItem?: GridItemProps;
    deliveryDatePicker?: DatePickerPresentationProps;
    pickUpDateGridItem?: GridItemProps;
    pickUpDatePicker?: DatePickerPresentationProps;
    // XNG-Changes
    headingText?: TypographyPresentationProps;
    innerContainer?: InjectableCss;
    shippingMethodContainer?: InjectableCss;
    deliveryDateWrapper?: InjectableCss;
    deliveryDateMessage?: TypographyPresentationProps;
    noteWrapper?: InjectableCss;
    carrierAccountField?: TextFieldPresentationProps;
    additionalDetailsField?: TextFieldPresentationProps;
    wholeOrderWrapper?: InjectableCss;
    wholeOrderCheckbox?: CheckboxProps;
    radioGroup?: RadioGroupProps;
    radioTextFieldWrapper?: InjectableCss;
    carrierContainer?: InjectableCss;
    serviceContainer?: InjectableCss;
    // XNG-Change
    shipmentRadioGroup?: RadioGroupProps;
    shipmentRadio?: RadioProps;
    selectedShipmentRadio?: RadioProps;
    sectionWrapper?: InjectableCss;
    shippingCostBreakdownWrapper?: InjectableCss;
    shippingCostLabel?: TypographyPresentationProps;
    amountCell?: DataTableCellProps;
    dateCell?: DataTableCellProps;
    requestCertificationsWrapper?: InjectableCss;
    requestCertificationsLink?: LinkPresentationProps;
    requestCertificationsTooltip?: TooltipPresentationProps;
}

export const checkoutShippingCarrierServiceStyles: CheckoutShippingCarrierServiceStyles = {
    container: { gap: 20 },
    centeringWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            height: 450px;
        `,
    },
    noCarriersFoundGridItem: { width: 12 },
    carrierGridItem: { width: [6, 6, 6, 3, 3] },
    serviceGridItem: { width: [6, 6, 6, 3, 3] },
    deliveryDateGridItem: { width: [12, 12, 12, 6, 6] },
    pickUpDateGridItem: { width: [12, 12, 12, 6, 6] },
};

/**
 * @deprecated Use checkoutShippingCarrierServiceStyles instead.
 */
export const checkoutShippingCarrierService = checkoutShippingCarrierServiceStyles;
const styles = checkoutShippingCarrierServiceStyles;
export interface ShippingCost {
    Key: string;
    Label: string;
    RequestedByDate: string;
    Amount: number;
    AmountDisplay: string;
}

const CheckoutReviewAndSubmitCarrierService: FC<Props> = ({
    cart,
    cartSettings,
    setRequestedDeliveryDate,
    setRequestedPickUpDate,
    showShippingMethod,
    setShippingMethod,
    showPickUpDate,
    setIsPreloadingData,
    allowEmptyShipping,

    // customization
    cartIsLoading,
    carrierAccount,
    additionalDetails,
    setCarrierAccount,
    setAdditionalDetails,
    requestedDeliveryDate,
    requestedPickupDate,
    billTo,
    loadBillTo,
    customSettings,
    // XNG-Change
    canHaveScheduledOrders,
    saveScheduledLines,
    scheduleOrderPageUrl,
    shipmentScheduleFromState,
    isShippingTBD,
}) => {
    const [selectedCarrier, setSelectedCarrier] = useState<CarrierDto | null>(null);
    const [selectedShipVia, setSelectedShipVia] = useState<ShipViaDto | null>(null);
    const [carrierError, setCarrierError] = React.useState("");
    const [carrierAccountError, setCarrierAccountError] = React.useState("");
    const [additionalDetailsError, setAdditionalDetailsError] = React.useState("");
    const currentCarrierIdx = cart?.carriers?.findIndex(c => c.id === selectedCarrier?.id);
    // <XNG-Change>
    const dispatch = useAppDispatch();
    const history = useHistory();
    const [shipmentSchedule, setShipmentSchedule] = React.useState(
        shipmentScheduleFromState || cart?.properties.shipmentDateType || shipmentDateTypeValues.ShipsAsap,
    );
    const [tempShipmentSchedule, setTempShipmentSchedule] = React.useState("");
    const [showFormErrors, setShowFormErrors] = React.useState(false);
    const [dateError, setDateError] = React.useState("");
    const [requestCertificationsModalIsOpen, setRequestCertificationsModalIsOpen] = useState(false);
    // </XNG-Change>

    // XNG-Change: guards against ScheduledDates ever being set on a Quote cart
    useEffect(() => {
        const resolvedShipmentSchedule =
            cart?.type === CART_TYPES.QUOTE && shipmentSchedule === shipmentDateTypeValues.ScheduledDates
                ? shipmentDateTypeValues.ShipsAsap
                : shipmentSchedule;
        if (resolvedShipmentSchedule !== shipmentSchedule) {
            updateShipment(resolvedShipmentSchedule);
        }
    }, [shipmentSchedule, cart?.type]);

    // checking and validating before going to the next step
    const { preventStepperChange } = useContext(StepperContext);

    const isRequestedDeliveryDate = () =>
        cart?.fulfillmentMethod === FulfillmentMethod.Ship && shipmentSchedule === shipmentDateTypeValues.OneDate;

    const isRequestedDeliveryDateRef = useRef(isRequestedDeliveryDate);
    isRequestedDeliveryDateRef.current = isRequestedDeliveryDate;

    useEffect(() => {
        if (!billTo && cart?.billToId) {
            loadBillTo({ billToId: cart?.billToId });
        }
    }, []);

    useEffect(() => {
        if (cart) {
            const selectedCarrier = cart?.carriers?.find(c => c.id === cart?.carrier?.id) || cart?.carrier;
            setSelectedCarrier(selectedCarrier);
            setSelectedShipVia(cart?.shipVia);
        }
    }, [cart?.carrier, cart?.shipVia]);

    useEffect(() => {
        const defaultCarrierIdx = cart?.carriers?.findIndex(c => c.description === cart?.properties.billToCarrier);
        const defaultCA = (defaultCarrierIdx === 1 ? billTo?.properties.carrierAccounts : "") as string;
        const defaultAD = (defaultCarrierIdx === 2 ? billTo?.properties.carrierAccounts : "") as string;
        if (!carrierAccount && (cart?.properties.carrierAccount || defaultCA)) {
            setCarrierAccount(cart?.properties.carrierAccount || defaultCA);
        }
        if (!carrierAccount && (cart?.properties.additionalDetails || defaultAD)) {
            setAdditionalDetails(cart?.properties.additionalDetails || defaultAD);
        }

        // XNG-Change: give initial value for shipment schedule radio
        if (cart?.properties?.shipmentDateType && !shipmentScheduleFromState) {
            setShipmentSchedule(cart.properties.shipmentDateType);
            dispatch({
                type: "xCheckout/setShipmentSchedule",
                shipmentSchedule: cart.properties.shipmentDateType,
            });
        }
    }, [cart]);

    const shippingCostBreakdown = useMemo<Record<string, ShippingCost>>(() => {
        const json = cart?.properties?.shippingCostBreakdown;
        if (!json) {
            return {};
        }

        const parsed = JSON.parse(json) as ShippingCost[];

        const newShippingCostBreakdown = parsed.reduce(
            (acc, item) => {
                acc[item.Label] = item;
                return acc;
            },
            {} as Record<string, ShippingCost>,
        );

        dispatch({
            type: "xCheckout/setShippingCostBreakdown",
            shippingCostBreakdown: newShippingCostBreakdown,
        });

        return newShippingCostBreakdown;
    }, [cart?.properties?.shippingCostBreakdown]);

    const carrierChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        const selectedCarrier = cart?.carriers?.find(c => c.id === event.currentTarget.value);

        if (!selectedCarrier) {
            return;
        }

        setIsPreloadingData({ isPreloadingData: true });
        setSelectedCarrier(selectedCarrier);

        let shipVia;
        // if (BLUEPRINT_NAME === "eisinc"  ||  BLUEPRINT_NAME === "midpoint" && currentCarrierIdx === 0) { // && cart?.properties?.billToCarrier === cart?.carrier?.description
        //     const shipCodeForIncoTermsDisplay = customSettings?.shipCodeForIncoTermsDisplay?.split(",");
        //     const selectedShipViaCode = shipCodeForIncoTermsDisplay.find((i:string) => i.includes(cart?.properties?.shipCode));
        //     if (selectedShipViaCode) {
        //         cart?.carriers;
        //         // we don't have access to carrier code to select with it.
        //         // this case need more discussion to set shipVia
        //     }
        // }
        if (!shipVia) {
            shipVia = selectedCarrier.shipVias?.find(i => i.isDefault) || selectedCarrier.shipVias![0];
        }

        setCarrierError("");

        setSelectedShipVia(shipVia);
        setShippingMethod({
            carrier: selectedCarrier,
            shipVia,
            onComplete: () => {
                setIsPreloadingData({ isPreloadingData: false });
            },
        });
    };

    const shipViaChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        if (!selectedCarrier) {
            return;
        }
        const selectedShipVia = selectedCarrier.shipVias!.find(s => s.id === event.currentTarget.value)!;
        setIsPreloadingData({ isPreloadingData: true });
        setSelectedShipVia(selectedShipVia);
        setShippingMethod({
            carrier: selectedCarrier,
            shipVia: selectedShipVia,
            onComplete: () => {
                setIsPreloadingData({ isPreloadingData: false });
            },
        });
    };

    const handleRequestDeliveryDateChanged = ({ selectedDay, selectedDayDisabled }: DatePickerState) => {
        setRequestedDeliveryDate({
            requestedDeliveryDate: selectedDay,
            requestedDeliveryDateDisabled: selectedDayDisabled,
        });

        saveScheduledLines({
            model: {
                shipAsapAll: false,
                shipOneRequestedDateAll: true,
                oneRequestedDate: convertDateToDateOnlyString(selectedDay),
                schedules: [],
            },
        });
        if (selectedDay) {
            setDateError("");
        }
    };

    const validateCarrier = (from: number, to: number) => {
        if (from === 2 && to >= 3) {
            if (
                cart?.fulfillmentMethod === FulfillmentMethod.Ship &&
                shipmentSchedule === shipmentDateTypeValues.OneDate
            ) {
                const isRequestedDeliveryDate = isRequestedDeliveryDateRef.current();

                const isValid =
                    isRequestedDeliveryDate && (requestedDeliveryDate || cart?.requestedDeliveryDateDisplay);

                if (!isValid) {
                    setShowFormErrors(!isValid);
                    setDateError(siteMessage("Custom_Checkout_RequestedDeliveryDate_Required") as string);
                    return true;
                }
            }

            if (showShippingMethod && (currentCarrierIdx === -1 || !selectedCarrier)) {
                setCarrierError(siteMessage("Custom_Checkout_CarrierAccount_Required") as string);
                return true;
            }

            setCarrierError("");

            if (selectedCarrier?.description === "Collect" && carrierAccount.length < 1) {
                setCarrierAccountError(siteMessage("Checkout_CarrierAccount_Required") as string);
                return true;
            }
            setCarrierAccountError("");

            if (selectedCarrier?.description === "Special Delivery Request" && additionalDetails.length < 1) {
                setAdditionalDetailsError(siteMessage("Checkout_AdditionalDetails_Required") as string);
                return true;
            }
            setAdditionalDetailsError("");
        }
    };
    const validateCarrierRef = useRef(validateCarrier);
    validateCarrierRef.current = validateCarrier;

    useEffect(() => preventStepperChange(validateCarrierRef, 2), []);

    const doSetCarrierAccount = (val: string) => {
        setCarrierAccount(val);
        setCarrierAccountError("");
    };
    const doSetAdditionalDetails = (val: string) => {
        setAdditionalDetails(val);
        setAdditionalDetailsError("");
    };

    if (!cart || (cart.lineCount && (!cart.cartLines || cart.cartLines.length === 0)) || !cart.carriers) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner color="primary" data-test-selector="checkoutShipping_carrierServiceLoading" />
            </StyledWrapper>
        );
    }

    // For Pickup
    if (!showShippingMethod) {
        return (
            <StyledWrapper data-test-selector="CheckoutReviewAndSubmitShippingInfo">
                <StyledWrapper {...styles.innerContainer} className="innerContainer">
                    {createWidgetElement("CheckoutReviewAndSubmit/CheckoutReviewAndSubmitShippingInfo", {
                        fields: {},
                    })}
                </StyledWrapper>
                <StyledWrapper
                    {...styles.innerContainer}
                    className="innerContainer"
                    data-test-selector="checkoutShippingRequestedPickupDate"
                >
                    <Typography {...styles.headingText} as="h3">
                        {translate("Pickup Details")}
                    </Typography>
                    <StyledWrapper {...styles.noteWrapper} className="innerContainer">
                        {createWidgetElement("CheckoutShipping/CheckoutShippingOrderNotesEntry", {
                            fields: {},
                        })}
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        );
    }

    // XNG-Change
    const shipmentScheduleChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        // minimize the call for apis
        const value = event.currentTarget.value;
        if (value !== shipmentDateTypeValues.OneDate) {
            setRequestedDeliveryDate({
                requestedDeliveryDate: undefined,
                requestedDeliveryDateDisabled: false,
            });
        }
        const cartShipmentDate = cart?.properties?.shipmentDateType;
        const hasScheduledDeliveries = parseBoolean(cart.properties.hasScheduledDeliveries);
        if (shipmentSchedule === shipmentDateTypeValues.ShipsAsap) {
            updateShipmentState(value);
        } else if (shipmentSchedule === shipmentDateTypeValues.OneDate) {
            if (value === shipmentDateTypeValues.ShipsAsap) {
                if (cartShipmentDate === shipmentDateTypeValues.ShipsAsap) {
                    updateShipmentState(value);
                } else {
                    updateShipment(value);
                }
            } else {
                updateShipmentState(value);
            }
        } else if (value === shipmentDateTypeValues.ShipsAsap) {
            if (hasScheduledDeliveries) {
                setTempShipmentSchedule(value);
                dispatch({ type: "xCommon/update", data: { key: "isChangeScheduleModalOpen", value: true } });
                return;
            }
            if (cartShipmentDate === shipmentDateTypeValues.ShipsAsap) {
                updateShipmentState(value);
            } else {
                updateShipment(value);
            }
        } else if (hasScheduledDeliveries) {
            setTempShipmentSchedule(value);
            dispatch({ type: "xCommon/update", data: { key: "isChangeScheduleModalOpen", value: true } });
        } else {
            updateShipmentState(value);
        }
    };

    const updateShipmentState = (selectedOption: string) => {
        setShipmentSchedule(selectedOption);
        dispatch({
            type: "xCheckout/setShipmentSchedule",
            shipmentSchedule: selectedOption,
        });
    };

    const updateShipment = (selectedOption: string) => {
        let model;
        if (selectedOption === shipmentDateTypeValues.ScheduledDates) {
            model = {
                shipAsapAll: false,
                shipOneRequestedDateAll: false,
                oneRequestedDate: "",
                schedules: [],
            };
        } else if (selectedOption === shipmentDateTypeValues.ShipsAsap) {
            model = {
                shipAsapAll: true,
                shipOneRequestedDateAll: false,
                oneRequestedDate: "",
                schedules: [],
            };
        } else {
            model = {
                shipAsapAll: false,
                shipOneRequestedDateAll: true,
                oneRequestedDate: "",
                schedules: [],
            };
        }

        saveScheduledLines({
            model,
            onSuccess() {
                setShipmentSchedule(selectedOption);
                dispatch({
                    type: "xCheckout/setShipmentSchedule",
                    shipmentSchedule: selectedOption,
                });
                setTempShipmentSchedule("");
                dispatch({ type: "xCommon/update", data: { key: "isChangeScheduleModalOpen", value: false } });
            },
        });
    };

    const handleCancel = () => {
        setTempShipmentSchedule("");
    };

    const handleContinue = () => {
        updateShipment(tempShipmentSchedule);
    };

    const goToScheduleOrderPage = () => {
        history.push(scheduleOrderPageUrl);
    };
    /**
     * XNG-Changes
     * Get Tomorrow Date
     */
    const tomorrow = new Date();
    tomorrow.setDate(new Date().getDate() + 1);

    const StyledForm = getStyledWrapper("form");

    // <XNG-Change>
    const closeRequestCertificationsModalHandler = () => {
        setRequestCertificationsModalIsOpen(false);
    };

    const openRequestCertificationsModalHandler = (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        setRequestCertificationsModalIsOpen(true);
    };
    // </XNG-Change>

    // XNG-Changes
    return (
        <StyledForm {...styles.container}>
            {cart.carriers?.length === 0 && !allowEmptyShipping && (
                <StyledWrapper {...styles.innerContainer} className="innerContainer">
                    <Typography {...styles.headingText} as="h3">
                        {translate("Shipping Method")}
                    </Typography>
                    <Typography {...styles.noCarriersFoundText}>
                        {siteMessage("ReviewAndPay_NoCarriersFound")}
                    </Typography>
                </StyledWrapper>
            )}

            <StyledWrapper {...styles.innerContainer} className="innerContainer">
                <Typography {...styles.headingText} as="h3">
                    {translate("Shipment Schedule")}
                </Typography>
                <StyledWrapper {...styles.deliveryDateWrapper}>
                    <RadioGroup
                        {...styles.shipmentRadioGroup}
                        value={shipmentSchedule}
                        onChangeHandler={shipmentScheduleChangeHandler}
                        horizontal={true}
                    >
                        <Radio
                            {...(shipmentSchedule === shipmentDateTypeValues.ShipsAsap
                                ? styles.selectedShipmentRadio
                                : styles.shipmentRadio)}
                            value={shipmentDateTypeValues.ShipsAsap}
                        >
                            {translate("Ships ASAP")}
                        </Radio>
                        <Radio
                            {...(shipmentSchedule === shipmentDateTypeValues.OneDate
                                ? styles.selectedShipmentRadio
                                : styles.shipmentRadio)}
                            value={shipmentDateTypeValues.OneDate}
                        >
                            {translate("Request Delivery Date")}
                        </Radio>
                        {canHaveScheduledOrders && (
                            <Radio
                                {...(shipmentSchedule === shipmentDateTypeValues.ScheduledDates
                                    ? styles.selectedShipmentRadio
                                    : styles.shipmentRadio)}
                                value={shipmentDateTypeValues.ScheduledDates}
                            >
                                {translate("Schedule Multiple Shipments")}
                            </Radio>
                        )}
                    </RadioGroup>
                    {cartSettings.canRequestDeliveryDate && shipmentSchedule === shipmentDateTypeValues.OneDate && (
                        <>
                            <Typography {...styles.deliveryDateMessage}>
                                {siteMessage("Shipping_Request_Delivery_Date_Subcopy")}
                            </Typography>
                            <DatePicker
                                label={translate("Request Ship Date")}
                                {...styles.deliveryDatePicker}
                                selectedDay={cart.requestedDeliveryDateDisplay! || requestedDeliveryDate}
                                dateTimePickerProps={{
                                    minDate: tomorrow,
                                    maxDate: addDays(new Date(), cartSettings.maximumDeliveryPeriod),
                                    monthAriaLabel: translate("Request delivery date month"),
                                    dayAriaLabel: translate("Request delivery date day"),
                                    yearAriaLabel: translate("Request delivery date year"),
                                    ...styles.deliveryDatePicker?.dateTimePickerProps,
                                }}
                                data-test-selector="checkoutReviewAndSubmit_deliveryDate"
                                onDayChange={({ selectedDay, selectedDayDisabled }) => {
                                    handleRequestDeliveryDateChanged({
                                        selectedDay,
                                        selectedDayDisabled,
                                    } as DatePickerState);
                                }}
                                required={true}
                                error={dateError}
                            />
                        </>
                    )}

                    {cartSettings.canRequestDeliveryDate &&
                        shipmentSchedule === shipmentDateTypeValues.ScheduledDates && (
                            <StyledWrapper {...styles.sectionWrapper}>
                                <Typography {...styles.deliveryDateMessage}>
                                    {siteMessage("Shipping_Multi_Date_Subcopy")}
                                </Typography>
                                <StyledWrapper>
                                    <Button type="button" sizeVariant="small" onClick={goToScheduleOrderPage}>
                                        {parseBoolean(cart.properties["hasScheduledDeliveries"])
                                            ? translate("Edit Shipment Schedule ")
                                            : translate("Schedule Shipments")}
                                    </Button>
                                </StyledWrapper>
                            </StyledWrapper>
                        )}
                </StyledWrapper>
            </StyledWrapper>

            {((cart.carriers && cart.carriers?.length > 0) || Object.keys(shippingCostBreakdown)?.length > 0) && (
                <StyledWrapper {...styles.innerContainer} className="innerContainer">
                    <Typography {...styles.headingText} as="h3">
                        {translate("Shipping Method")}
                    </Typography>
                    <StyledWrapper {...styles.shippingMethodContainer}>
                        {cart.carriers && cart.carriers?.length > 0 && (
                            <>
                                <StyledWrapper {...styles.carrierContainer}>
                                    <RadioGroup
                                        label={translate("Select Carrier")}
                                        required={true}
                                        name="carrier"
                                        value={selectedCarrier?.id || ""}
                                        onChangeHandler={carrierChangeHandler}
                                        data-test-selector="checkoutReviewAndSubmit_carrierSelect"
                                        error={carrierError}
                                        {...styles.radioGroup}
                                    >
                                        {cart.carriers!.map((c, idx) => {
                                            const id = c.id!.toString();
                                            let radio = (
                                                <Radio
                                                    key={id}
                                                    value={id}
                                                    className={xClassNames.append(idx === 0 && "first-item")}
                                                >
                                                    {c.description}
                                                </Radio>
                                            );

                                            // check for collect
                                            if (currentCarrierIdx === idx && c.description === "Collect") {
                                                radio = (
                                                    <StyledWrapper
                                                        key={`Collect-${id}`}
                                                        {...styles.radioTextFieldWrapper}
                                                    >
                                                        {radio}
                                                        <TextField
                                                            label={`${translate("Carrier Account")}:`}
                                                            placeholder={translate("Carrier Account")}
                                                            name="carrierAccount"
                                                            value={carrierAccount}
                                                            disabled={false}
                                                            maxLength={28}
                                                            onChange={e => doSetCarrierAccount(e.currentTarget.value)}
                                                            error={carrierAccountError}
                                                            data-test-selector="carrierAccount"
                                                            {...({
                                                                info: siteMessage("Custom_CarrierAccount_Info"),
                                                            } as any)}
                                                            {...styles.carrierAccountField}
                                                        />
                                                    </StyledWrapper>
                                                );
                                            } else if (
                                                currentCarrierIdx === idx &&
                                                c.description === "Special Delivery Request"
                                            ) {
                                                radio = (
                                                    <StyledWrapper
                                                        key={`SpecialDeliveryRequest-${id}`}
                                                        {...styles.radioTextFieldWrapper}
                                                    >
                                                        {radio}
                                                        <TextField
                                                            label={`${translate("Additional Details")}:`}
                                                            placeholder={translate("Additional Details")}
                                                            name="additionalDetails"
                                                            value={additionalDetails}
                                                            disabled={false}
                                                            maxLength={28}
                                                            onChange={e =>
                                                                doSetAdditionalDetails(e.currentTarget.value)
                                                            }
                                                            error={additionalDetailsError}
                                                            data-test-selector="additionalDetails"
                                                            {...({
                                                                info: siteMessage("Custom_AdditionalDetails_Info"),
                                                            } as any)}
                                                            {...styles.additionalDetailsField}
                                                        />
                                                    </StyledWrapper>
                                                );
                                            }
                                            return radio;
                                        })}
                                    </RadioGroup>
                                </StyledWrapper>
                                {selectedCarrier?.description !== "Special Delivery Request" && (
                                    <StyledWrapper {...styles.serviceContainer}>
                                        <RadioGroup
                                            label={translate("Select Service")}
                                            name="shipVia"
                                            value={selectedShipVia?.id || ""}
                                            onChangeHandler={shipViaChangeHandler}
                                            data-test-selector="checkoutReviewAndSubmit_shippingServiceSelect"
                                            {...styles.radioGroup}
                                        >
                                            {selectedCarrier &&
                                                selectedCarrier.shipVias!.map(s => {
                                                    const id = s.id.toString();
                                                    const updatedDescription = s.description.replace(
                                                        /(.*)(\$.*)/g,
                                                        "$1 <b>$2</b>",
                                                    );
                                                    return (
                                                        <Radio key={id} value={id}>
                                                            <span
                                                                // eslint-disable-next-line react/no-danger
                                                                dangerouslySetInnerHTML={{
                                                                    __html: updatedDescription,
                                                                }}
                                                            />
                                                        </Radio>
                                                    );
                                                })}
                                        </RadioGroup>
                                    </StyledWrapper>
                                )}
                            </>
                        )}
                        {Object.keys(shippingCostBreakdown)?.length > 0 && (
                            <StyledWrapper {...styles.shippingCostBreakdownWrapper}>
                                <Typography {...styles.shippingCostLabel}>
                                    {translate("Shipping Cost Breakdown")}
                                </Typography>
                                <DataTable>
                                    <DataTableBody>
                                        {Object.values(shippingCostBreakdown)?.map((item: any) => (
                                            <DataTableRow key={item.Key}>
                                                <DataTableCell {...styles.dateCell}>{item.Label}</DataTableCell>
                                                <DataTableCell {...styles.amountCell}>
                                                    {item.AmountDisplay}
                                                </DataTableCell>
                                            </DataTableRow>
                                        ))}
                                        <DataTableRow>
                                            <DataTableCell {...styles.dateCell}>{translate("Total")}</DataTableCell>
                                            <DataTableCell {...styles.amountCell}>
                                                {isShippingTBD || cart.properties?.shipping}
                                            </DataTableCell>
                                        </DataTableRow>
                                    </DataTableBody>
                                </DataTable>
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            )}
            <StyledWrapper
                {...styles.innerContainer}
                className="innerContainer"
                data-test-selector="checkoutShippingRequestedDeliveryDate"
            >
                <Typography {...styles.headingText} as="h3">
                    {translate("Shipping Details")}
                </Typography>
                <StyledWrapper {...styles.noteWrapper}>
                    {createWidgetElement("CheckoutShipping/CheckoutShippingOrderNotesEntry", {
                        fields: {},
                    })}
                </StyledWrapper>
                <StyledWrapper {...styles.requestCertificationsWrapper}>
                    <Link {...styles.requestCertificationsLink} onClick={openRequestCertificationsModalHandler}>
                        {translate("Request Certifications")}
                    </Link>
                    <Tooltip
                        text={siteMessage("Custom_Request_Certifications_Message")}
                        {...styles.requestCertificationsTooltip}
                    />
                    <RequestCerificationsModal
                        cart={cart}
                        closeModalHandler={closeRequestCertificationsModalHandler}
                        isOpen={requestCertificationsModalIsOpen}
                    />
                </StyledWrapper>
            </StyledWrapper>

            <ChangeScheduleModal handleCancel={handleCancel} handleContinue={handleContinue} />
        </StyledForm>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CheckoutReviewAndSubmitCarrierService),
    definition: {
        displayName: "Carrier & Service",
        group: "Checkout - Review & Submit",
        allowedContexts: ["CheckoutReviewAndSubmitPage"],
    },
};

export default widgetModule;
